import React, { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { filterObjectProps } from "@audacia-hq/shared/utils";
import dayjs from "dayjs";

import { useMember } from "../../context/MemberContext";
import InputDateField from "../form/InputDateField";

interface RequiredFields {
  pseudo?: boolean;
  birthday?: boolean;
}

interface DataTypes {
  pseudo?: string, birthday?: string
}

interface MissingMemberDataContext {
  managedFields: string[];
  initialValues: DataTypes;
  validationRules: { pseudo?: Yup.StringSchema, birthday?: Yup.StringSchema }
  inputs: (formik) => { [field: string]: (labelClassName?: string, inputClassName?: string) => React.ReactNode };
  onSubmit: (formData, formikHelpers) => Promise<void | unknown>
}

const useMissingMemberData = ({
  pseudo, birthday,
}: RequiredFields): MissingMemberDataContext => {
  const { member, handleUpdateMember } = useMember();
  const { t } = useTranslation("member");

  const [managedFields, setManagedFields] = useState<string[]>([]);
  const [initialValues, setInitialValues] = useState({});
  const [validationRules, setValidationRules] = useState({});

  const onSubmit = useCallback(
    member && managedFields.length
      ? (formData, helpers) => new Promise((resolve, reject) => {
        handleUpdateMember(filterObjectProps(formData, managedFields)).then((res) => {
          if (res?.error?.messages?.includes("pseudo.alreadyUsed")) {
            helpers.setFieldError("pseudo", t("common:errors.pseudoAlreadyUsed"));
            reject(new Error("pseudo.alreadyUsed"));
          }
          resolve(undefined);
        });
      })
      : () => Promise.resolve(undefined),
    [member, managedFields],
  );

  const inputs = useCallback(
    (formik) => ({
      pseudo: managedFields.includes("pseudo")
        ? (labelClassName, inputClassName) => (
          <>
            <label htmlFor="pseudo" className={labelClassName}>
              {t("form.pseudo")}
              *
            </label>
            <input
              className={`input ${inputClassName} ${formik.errors.pseudo ? "border-red-500 border" : ""}`}
              disabled={!!member?.profile.pseudo}
              id="pseudo"
              defaultValue={formik.values.pseudo}
              value={formik.values.pseudo}
              onBlur={() => {
                formik.setFieldValue("pseudo", formik.values.pseudo?.trim());
              }}
              onChange={(e) => {
                const v = e.target.value.normalize("NFD")
                  .replace(/[^a-zA-Z0-9 _\- \s\t \u00C0-\u00D6 \u00D8-\u00F6 \u00F8-\u01BF]+/g, "")
                  .replace(/[\s\t]{2,}/g, " ");
                formik.setFieldValue("pseudo", v);
              }}
            />
            {formik.errors.pseudo ? (
              <span className="text-red-500">{formik.errors.pseudo}</span>
            ) : (
              <span className="text-sm text-gray-500">
                {t("form.subtext.pseudo")}
              </span>
            )}
          </>
        )
        : undefined,
      birthday: managedFields.includes("birthday")
        ? (labelClassName, inputClassName) => (
          <>
            <label htmlFor="birthday" className={labelClassName}>
              {t("form.birthday")}
              *
            </label>
            <InputDateField
              name="birthday"
              className={`w-full justify-between ${inputClassName}`}
              initialValue={formik.values.birthday}
              setFieldValue={formik.setFieldValue}
              setFieldTouched={formik.setFieldTouched}
            />
            {formik.errors.birthday ? (
              <span className="text-red-500">{formik.errors.birthday}</span>
            ) : (
              <span className="text-sm text-gray-500">
                {t("form.subtext.birthday")}
              </span>
            )}
          </>
        )
        : undefined,
    }),
    [member, managedFields],
  );

  useEffect(() => {
    if (!member) return;
    const fields = {
      pseudo: pseudo && !member.profile?.pseudo,
      birthday: birthday && !member.birthday,
    };
    setManagedFields([
      ...(fields.pseudo ? ["pseudo"] : []),
      ...(fields.birthday ? ["birthday"] : []),
    ]);
    setInitialValues({
      pseudo: fields.pseudo ? "" : undefined,
      birthday: fields.birthday ? "" : undefined,
    });
    setValidationRules({
      pseudo: fields.pseudo ? Yup.string().required(t("common:errors.required")).min(3, t("common:errors.pseudoMinLength", { length: 3 })) : undefined,
      birthday: fields.birthday ? Yup.string()
        .required(t("common:errors.required"))
        .test("is-over-18", t("common:errors.over18"), (value) => dayjs().diff(dayjs(value), "year") >= 18)
        : undefined,
    });
  }, [member]);

  return {
    managedFields,
    initialValues,
    validationRules,
    inputs,
    onSubmit,
  };
};

export default useMissingMemberData;
